// Banner + Modal para Agendar Cita de Montaje con el cliente
import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  CalendarPlus, CheckCircle2, Copy, MessageCircle, RefreshCw, Loader2, X as IconX,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

const DIAS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const MESES_CORTO = ["", "ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

function fechaCorta(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso + "T00:00:00");
    return `${DIAS[d.getDay()]} ${d.getDate()} ${MESES_CORTO[d.getMonth() + 1]}`;
  } catch { return iso; }
}

export function PropuestaMontajeBanner({ peticion, onChange }) {
  const [open, setOpen] = useState(false);
  const prop = peticion?.propuesta_montaje;
  const tieneAlguno =
    prop && (prop.estado === "pendiente" || prop.estado === "aceptada" || prop.estado === "rechazada");

  return (
    <>
      {!tieneAlguno && (
        <div className="mb-6 rounded-xl p-4 border border-violet-500/30 bg-violet-500/5 flex items-center justify-between gap-3 flex-wrap"
             data-testid="banner-agendar-cita">
          <div className="text-sm">
            <span className="text-violet-200 font-semibold">¿Cliente de calle?</span>
            <span className="text-zinc-400 ml-2">Propón fechas de montaje y el cliente las acepta vía WhatsApp.</span>
          </div>
          <Button size="sm" onClick={() => setOpen(true)}
            data-testid="proponer-fechas-btn"
            className="bg-violet-500 hover:bg-violet-400 text-zinc-900 font-bold">
            <CalendarPlus className="w-4 h-4 mr-1.5" /> Proponer fechas
          </Button>
        </div>
      )}

      {prop?.estado === "pendiente" && (
        <div className="mb-6 rounded-xl p-4 border-2 border-amber-500/40 bg-amber-500/10"
             data-testid="banner-propuesta-pendiente">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div className="text-sm font-semibold text-amber-200">
              📅 Propuesta enviada al cliente · esperando respuesta
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setOpen(true)} variant="outline"
                data-testid="reenviar-propuesta-btn"
                className="bg-transparent border-amber-500/40 text-amber-200 hover:bg-amber-500/10">
                <RefreshCw className="w-3 h-3 mr-1.5" /> Reenviar / cambiar fechas
              </Button>
              <CancelarBtn peticionId={peticion.id} onDone={onChange} />
            </div>
          </div>
          <div className="text-xs text-zinc-300 flex flex-wrap gap-2">
            {(prop.fechas || []).map((f, i) => (
              <span key={f} className="bg-zinc-900/60 rounded px-2 py-0.5 border border-zinc-800">
                {i + 1}ª · {fechaCorta(f)}
              </span>
            ))}
            <span className="text-zinc-500 text-[11px]">
              · {prop.visto_count ? `Visto ${prop.visto_count}×` : "Sin abrir aún"}
            </span>
          </div>
          {prop.mensaje_extra && <div className="text-[11px] text-zinc-400 italic mt-1">"{prop.mensaje_extra}"</div>}
        </div>
      )}

      {prop?.estado === "aceptada" && (
        <div className="mb-6 rounded-xl p-4 border-2 border-emerald-500/40 bg-emerald-500/10"
             data-testid="banner-propuesta-aceptada">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="text-sm font-bold text-emerald-200 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> Cliente confirmó fecha de montaje
              </div>
              <div className="text-base font-semibold text-emerald-300 mt-1">
                📅 {fechaCorta(prop.fecha_elegida)}
              </div>
              {prop.nombre_cliente_respuesta && (
                <div className="text-[11px] text-zinc-400 mt-0.5">Por {prop.nombre_cliente_respuesta}</div>
              )}
              {prop.notas_cliente && (
                <div className="text-[11px] text-zinc-300 mt-1 italic">"{prop.notas_cliente}"</div>
              )}
            </div>
            <Button size="sm" onClick={() => setOpen(true)} variant="outline"
              className="bg-transparent border-zinc-700">
              <RefreshCw className="w-3 h-3 mr-1.5" /> Cambiar fecha
            </Button>
          </div>
        </div>
      )}

      {prop?.estado === "rechazada" && (
        <div className="mb-6 rounded-xl p-4 border-2 border-rose-500/40 bg-rose-500/10"
             data-testid="banner-propuesta-rechazada">
          <div className="text-sm font-bold text-rose-200 mb-2">
            🔄 Cliente propone otras fechas
          </div>
          <div className="text-xs text-zinc-300 flex flex-wrap gap-2 mb-2">
            {(prop.fechas_alternativas || []).map((f) => (
              <span key={f} className="bg-zinc-900/60 rounded px-2 py-0.5 border border-rose-500/30">
                {fechaCorta(f)}
              </span>
            ))}
          </div>
          {prop.motivo_rechazo && (
            <div className="text-[11px] text-zinc-400 italic mb-2">Motivo: "{prop.motivo_rechazo}"</div>
          )}
          <Button size="sm" onClick={() => setOpen(true)}
            data-testid="renovar-propuesta-btn"
            className="bg-violet-500 hover:bg-violet-400 text-zinc-900 font-bold">
            <CalendarPlus className="w-3 h-3 mr-1.5" /> Enviar nueva propuesta
          </Button>
        </div>
      )}

      {open && (
        <DialogProponerFechas
          peticion={peticion}
          fechasIniciales={prop?.fechas_alternativas || []}
          onClose={() => setOpen(false)}
          onDone={() => { setOpen(false); onChange && onChange(); }}
        />
      )}
    </>
  );
}

function CancelarBtn({ peticionId, onDone }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    if (!window.confirm("¿Cancelar la propuesta enviada? El enlace dejará de funcionar.")) return;
    setBusy(true);
    try {
      await api.post(`/peticiones/${peticionId}/cancelar-propuesta-montaje`);
      toast.success("Propuesta cancelada");
      onDone && onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  return (
    <Button size="sm" onClick={handle} disabled={busy} variant="outline"
      data-testid="cancelar-propuesta-btn"
      className="bg-transparent border-rose-500/40 text-rose-300 hover:bg-rose-500/10">
      {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <Trash2 className="w-3 h-3 mr-1.5" />}
      Cancelar
    </Button>
  );
}

function DialogProponerFechas({ peticion, fechasIniciales, onClose, onDone }) {
  // Pre-rellenar con alternativas del cliente si existen
  const seed = fechasIniciales && fechasIniciales.length > 0
    ? [...fechasIniciales.slice(0, 3), ...Array(Math.max(0, 3 - fechasIniciales.length)).fill("")]
    : ["", "", ""];
  const [fechas, setFechas] = useState(seed);
  const [mensaje, setMensaje] = useState("");
  const [resultado, setResultado] = useState(null);
  const [saving, setSaving] = useState(false);
  const tel = peticion?.contacto_telefono || peticion?.cliente_telefono || "";

  const enviar = async () => {
    const limpias = fechas.filter((f) => f && f.length === 10);
    if (limpias.length === 0) return toast.error("Indica al menos una fecha");
    setSaving(true);
    try {
      const { data } = await api.post(
        `/peticiones/${peticion.id}/proponer-fechas-montaje`,
        { fechas: limpias, mensaje_extra: mensaje || null }
      );
      setResultado(data);
      toast.success("Propuesta lista — envía el enlace al cliente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  const copiar = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success("Copiado");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = texto; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Copiado"); }
      catch { toast.error("Cópialo manualmente"); }
      document.body.removeChild(ta);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>📅 Proponer fechas de montaje</DialogTitle>
        </DialogHeader>

        {!resultado ? (
          <div className="space-y-3">
            <p className="text-xs text-zinc-400">
              Indica 1-3 fechas. El cliente las recibirá por WhatsApp con un enlace para elegir.
            </p>
            {fechas.map((f, i) => (
              <div key={i}>
                <Label className="text-xs">Fecha {i + 1}{i > 0 ? " (opcional)" : ""}</Label>
                <Input type="date" value={f}
                  onChange={(e) => {
                    const a = [...fechas]; a[i] = e.target.value; setFechas(a);
                  }}
                  data-testid={`propuesta-fecha-${i}`}
                  className="bg-zinc-900 border-zinc-800" />
              </div>
            ))}
            <div>
              <Label className="text-xs">Mensaje extra (opcional)</Label>
              <Textarea rows={2} value={mensaje} onChange={(e) => setMensaje(e.target.value)}
                placeholder="Ej. preferimos por la mañana"
                data-testid="propuesta-mensaje-extra"
                className="bg-zinc-900 border-zinc-800 text-sm" />
            </div>
            <Button onClick={enviar} disabled={saving}
              data-testid="generar-propuesta-btn"
              className="w-full bg-violet-500 hover:bg-violet-400 text-zinc-900 font-bold">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CalendarPlus className="w-4 h-4 mr-1.5" />}
              Generar enlace + WhatsApp
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="resultado-propuesta">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-2 text-xs text-emerald-200">
              ✅ Enlace generado — ahora envíalo al cliente
            </div>
            <div>
              <Label className="text-xs">Enlace público</Label>
              <div className="flex gap-2">
                <Input readOnly value={resultado.url}
                  data-testid="url-publica"
                  className="bg-zinc-900 border-zinc-800 text-xs font-mono" />
                <Button onClick={() => copiar(resultado.url)} size="sm"
                  data-testid="copiar-url-btn"
                  className="bg-zinc-800 hover:bg-zinc-700">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Mensaje WhatsApp (editable)</Label>
              <Textarea rows={8}
                defaultValue={resultado.mensaje_whatsapp}
                id="msg-wa-edit"
                data-testid="mensaje-wa"
                className="bg-zinc-900 border-zinc-800 text-xs" />
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Button
                  onClick={() => copiar(document.getElementById("msg-wa-edit").value)}
                  variant="outline"
                  data-testid="copiar-mensaje-btn"
                  className="bg-transparent border-zinc-700">
                  <Copy className="w-3 h-3 mr-1.5" /> Copiar mensaje
                </Button>
                <Button
                  onClick={() => {
                    const msg = document.getElementById("msg-wa-edit").value;
                    const num = (tel || "").replace(/\D/g, "");
                    const url = num
                      ? `https://wa.me/${num}?text=${encodeURIComponent(msg)}`
                      : `https://web.whatsapp.com/send?text=${encodeURIComponent(msg)}`;
                    window.open(url, "_blank");
                  }}
                  data-testid="abrir-whatsapp-btn"
                  className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold">
                  <MessageCircle className="w-3 h-3 mr-1.5" /> Abrir WhatsApp
                </Button>
              </div>
              {!tel && (
                <div className="text-[10px] text-amber-300 mt-1">
                  ⚠️ No hay teléfono del cliente — abre WhatsApp Web y elige el contacto.
                </div>
              )}
            </div>
            <Button onClick={onDone} variant="outline" className="w-full bg-transparent border-zinc-700"
              data-testid="cerrar-propuesta-dialog">
              <IconX className="w-3 h-3 mr-1.5" /> Cerrar
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
